import { useUser } from '@clerk/clerk-react'
import { useEffect, useRef } from 'react'
import { Userpilot } from 'userpilot'

/**
 * Stops Userpilot from carrying a learner past sign-out.
 *
 * Userpilot keeps the last identified user in browser storage, so without this
 * the signed-out page kept rendering that learner's checklist and trial banner,
 * and anything done before the next sign-in was attributed to them (a real gap
 * on the shared computers many learners use). On sign-out, `suppress` takes
 * down every running experience and pauses the SDK, and `clean` forgets the
 * stored user. Both are reversible, so signing back in without a page reload
 * only needs `unsuppress` before the next identify. `destroy` was avoided: it
 * also tears down the SDK's socket, which a later identify is not documented
 * to restore.
 *
 * Call this before `Userpilot.identify` runs in the same component, so the
 * unsuppress effect fires first on sign-in.
 */
export function useUserpilotSignOutReset(): void {
  const { isLoaded, isSignedIn } = useUser()
  const suppressedRef = useRef(false)

  useEffect(() => {
    if (!isLoaded) return

    if (isSignedIn) {
      if (suppressedRef.current) {
        Userpilot.unsuppress()
        suppressedRef.current = false
      }
      return
    }

    // Also covers a signed-out page load, which clears an identity left behind
    // by a session that ended without this tab open.
    Userpilot.suppress()
    Userpilot.clean()
    suppressedRef.current = true
  }, [isLoaded, isSignedIn])
}
